import { type ColorParams, TextElement, type TextStyle } from '@bynder-studio/render-core';
import { type ColorParamsWithHex, colorToHex, Gradient, type SolidColor, type Stroke } from '@bynder-studio/misc';
import type {
    BrandColor,
    BrandColorPalette,
    GradientBrandColor,
    SolidBrandColor,
} from 'packages/store/brandColors/types';
import { brandColor2ColorParams, rgbaToObject } from 'packages/common/editor/editorHelper';
import { hexToRgb, isGradientColor } from '../../../FormComponents/ColorPicker/utils';
import { toFloat } from '../../../../EditorAutocorrectRules';
import { AppliedStyles, CleanedRunStyle } from './types';

const toPercent = (v) => Math.round(toFloat(v) * 100);

export const fontSize = (fontSize: number) => (Number.isNaN(fontSize) ? 'Mixed' : fontSize + 'px');

export const leading = (leading: number) => {
    if (Number.isNaN(leading)) {
        return 'Mixed';
    } else if (leading === 1) {
        return 'Auto';
    }

    return toPercent(leading);
};

export const overridesText = (overrides) => `${overrides} override${overrides > 1 ? 's' : ''}`;

export const textStyleSubtext = (style?: TextStyle, overrides = 0) => {
    if (overrides) {
        return overridesText(overrides);
    } else if (style) {
        return `${fontSize(style.fontSize)} / ${leading(style.leading)}`;
    }

    return '';
};

export const collectStyleIds = (selectedElement?: TextElement) => {
    const styles = new Set<string | null>();

    if (!(selectedElement instanceof TextElement)) {
        return styles;
    }

    const { runs } = selectedElement.getTextProps();
    styles.add(null);
    runs.forEach((run) => styles.add(run.styleId || null));

    return styles;
};

export const collectElementStyleIds = (element?: TextElement) => {
    if (!(element instanceof TextElement)) {
        return new Set<string>();
    }

    const appliedStyles = collectStyleIds(element);

    return new Set([...([...appliedStyles].filter((s) => !!s) as string[]), ...element.textStyles]);
};

export const collectBrandColorIds = (selectedElement?: TextElement) => {
    const brandColors = new Set<number>();

    if (!(selectedElement instanceof TextElement)) {
        return brandColors;
    }

    const { runs } = selectedElement.getTextProps();
    runs.forEach((run) => {
        if (run.color && run.color.brandColorId) {
            brandColors.add(run.color.brandColorId);
        }
    });

    return brandColors;
};

export const collectCustomColors = (selectedElement?: TextElement) => {
    const list: Array<ColorParamsWithHex> = [];

    if (!(selectedElement instanceof TextElement)) {
        return list;
    }

    const customColors = new Set<string>();

    const { runs } = selectedElement.getTextProps();
    runs.forEach((run) => {
        if (run.color && !run.color.brandColorId) {
            const hexCode = colorToHex(run.color);
            const key = `${hexCode}${run.color.type}`;

            if (customColors.has(key)) {
                return;
            }

            customColors.add(key);
            list.push({ ...run.color, hexCode } as ColorParamsWithHex);
        }
    });

    return list;
};

export const collectElementBrandColorIds = (element?: TextElement) => {
    if (!(element instanceof TextElement)) {
        return new Set<number>();
    }

    const brandColors = collectBrandColorIds(element);

    return new Set([...brandColors, ...element.brandColors]);
};

export const collectStylIdsFromTemplate = (templateElement: any) => {
    const styles = new Set<string | null>();

    if (templateElement?.properties) {
        styles.add(templateElement.properties?.styleId || null);
        templateElement.properties.value.runs.forEach((run) => styles.add(run.styleId || null));
        (templateElement.properties.textStyles || []).forEach((styleId) => styles.add(styleId));
    }

    return styles;
};

export const toRGBA = (c: { red: number; green: number; blue: number; opacity: number }): string =>
    `rgba(${c.red}, ${c.green}, ${c.blue}, ${c.opacity})`;

export const collectColorsFromTemplate = (templateElement: any, palettes: BrandColorPalette[]) => {
    const styles = new Set<string | null>();
    const colors: ColorParams[] = [];

    if (templateElement?.properties?.fontColor) {
        if (isGradientColor(templateElement.properties.fontColor)) {
            const convertedColor = brandColor2ColorParams(
                templateElement.properties.fontColor as BrandColor,
            ) as Gradient;
            const key = convertedColor.stops.reduce(
                (acc, curr) => `${acc + toRGBA(curr)}`,
                `${convertedColor.brandColorId || ''}`,
            );

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(convertedColor);
            }
        } else {
            const key =
                toRGBA(templateElement.properties.fontColor) +
                (templateElement.properties.fontColor.brandColorId || '');

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(templateElement.properties.fontColor);
            }
        }
    }

    if (templateElement?.properties) {
        templateElement.properties.value.runs.forEach((run) => {
            if (run.color) {
                if (isGradientColor(run.color)) {
                    const convertedColor = brandColor2ColorParams(run.color as BrandColor) as Gradient;
                    const key = convertedColor.stops.reduce(
                        (acc, curr) => `${acc + toRGBA(curr)}`,
                        `${convertedColor.brandColorId || ''}`,
                    );

                    if (!styles.has(key)) {
                        styles.add(key);
                        colors.push(convertedColor);
                    }
                } else {
                    const key = toRGBA(run.color) + (run.color.brandColorId || '');

                    if (!styles.has(key)) {
                        styles.add(key);
                        colors.push(run.color);
                    }
                }
            }
        });
    }

    const byId: { [brandColorId: number]: BrandColor } = {};
    palettes.forEach((palette) => {
        palette.brandColors.forEach((color) => {
            if (!color.deleted) {
                byId[color.id] = color;
            }
        });
    });

    (templateElement?.properties?.brandColors || []).forEach((brandColorId: number) => {
        const brandColor = byId[brandColorId];

        if (!brandColor) {
            return;
        }

        if (isGradientColor(brandColor)) {
            const convertedColor = brandColor2ColorParams(brandColor as BrandColor) as ColorParams;
            // @ts-ignore
            const key = convertedColor.stops.reduce(
                (acc, curr) => acc + toRGBA(curr),
                `${convertedColor.brandColorId || ''}`,
            );

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(convertedColor);
            }
        } else {
            const bc = brandColor as SolidBrandColor;
            const color = {
                brandColorId: bc.id,
                ...rgbaToObject(hexToRgb(bc.hexCode, bc.alpha)),
            } as SolidColor;
            const key = toRGBA(color) + (color.brandColorId || '');

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(color);
            }
        }
    });

    return colors;
};

const getRunFillColor = (run: { color?: ColorParams }) => run.color;

export const collectColorsFromElement = (
    element: TextElement,
    palettes: BrandColorPalette[],
    getColor: (run: { color?: ColorParams; stroke?: Stroke }) => ColorParams | null | undefined = getRunFillColor,
) => {
    const styles = new Set<string | null>();
    const colors: ColorParams[] = [];
    const { runs } = element.getTextProps();

    const byId: { [brandColorId: number]: BrandColor } = {};
    palettes.forEach((palette) => {
        palette.brandColors.forEach((color) => {
            if (!color.deleted) {
                byId[color.id] = color;
            }
        });
    });

    runs.forEach((run) => {
        const runColor = getColor(run);

        if (runColor) {
            let key;

            if (isGradientColor(runColor)) {
                const c = runColor as Gradient;
                key = c.stops.reduce((acc, curr) => acc + toRGBA(curr), `${c.brandColorId}`);
            } else {
                const c = runColor as SolidColor;
                key = toRGBA(c) + c.brandColorId;
            }

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(runColor);
            }
        }
    });

    (element.brandColors || []).forEach((brandColorId: number) => {
        const brandColor = byId[brandColorId];

        if (!brandColor) {
            return;
        }

        if (isGradientColor(brandColor)) {
            const bc = brandColor as GradientBrandColor;
            const convertedColor = brandColor2ColorParams(bc) as Gradient;
            const newColor = {
                brandColorId: bc.id,
                ...convertedColor,
            } as unknown as Gradient;

            const key = newColor.stops.reduce((acc, curr) => acc + toRGBA(curr), `${newColor.brandColorId}`);

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(newColor);
            }
        } else {
            const bc = brandColor as SolidBrandColor;
            const color = {
                brandColorId: bc.id,
                ...rgbaToObject(hexToRgb(bc.hexCode, bc.alpha)),
            } as SolidColor;
            const key = toRGBA(color) + color.brandColorId;

            if (!styles.has(key)) {
                styles.add(key);
                colors.push(color);
            }
        }
    });

    return colors;
};

export const cleanUpRawStyles = (rawStyles: AppliedStyles, runIdx: number) => {
    const obj = {} as CleanedRunStyle;
    Object.entries(rawStyles).forEach(([key, value]) => {
        if (['styleId', 'color'].includes(key)) {
            return;
        }

        obj[key] = key === 'fontId' ? Number(value[runIdx]) : value[runIdx];
    });

    return obj;
};

export const textStyleToRunStyle = (textStyle: TextStyle) => {
    const { uuid, name, ...styles } = textStyle;
    // TODO: This will be fixed after leading fontId property to common type!
    (styles as any).fontId = String(styles.fontId);

    return styles;
};
